/**
 * Secure API Configuration
 * Enforces security best practices and HTTPS communication
 */

// Environment-based configuration
const getSecureBaseURL = () => {
  if (typeof window !== 'undefined') {
    // Client-side: enforce HTTPS in production
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;

    // Force HTTPS in production
    if (process.env.NODE_ENV === 'production' && protocol !== 'https:') {
      console.warn('⚠️ SECURITY WARNING: Switching to HTTPS for security');
      return `https://${hostname}${port ? `:${port}` : ''}/api`;
    }

    return `${protocol}//${hostname}${port ? `:${port}` : ''}/api`;
  }

  // Server-side fallback
  return process.env.REACT_APP_API_URL || '/api';
};

/**
 * Security configuration constants
 */
export const SECURITY_CONFIG = {
  // HTTPS enforcement
  enforceHTTPS: process.env.NODE_ENV === 'production',

  // Request limits
  maxRequestSize: 1024 * 1024, // 1MB
  maxResponseSize: 10 * 1024 * 1024, // 10MB
  maxQueryParams: 20,
  maxHeaderSize: 8192, // 8KB

  // Timeouts
  defaultTimeout: 30000, // 30 seconds
  retryTimeout: 60000, // 1 minute for retries

  // Rate limiting (client-side)
  rateLimits: {
    GET: { maxRequests: 100, windowMs: 60000 }, // 100/minute
    POST: { maxRequests: 20, windowMs: 60000 },  // 20/minute (even though disabled)
    PUT: { maxRequests: 10, windowMs: 60000 },   // 10/minute (even though disabled)
    DELETE: { maxRequests: 5, windowMs: 60000 }  // 5/minute (even though disabled)
  },

  // Request queue limits
  maxConcurrentRequests: 5,
  maxQueueSize: 50,

  // CSRF protection
  csrfEnabled: true,
  csrfTokenHeader: 'X-CSRF-Token',

  // Content Security Policy headers
  securityHeaders: {
    'X-Content-Type-Options': 'nosniff',
    'Content-Security-Policy': 'frame-ancestors *',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains'
  },

  // Allowed content types for responses
  allowedContentTypes: [
    'application/json',
    'application/hal+json',
    'text/plain',
    'text/json'
  ],

  // Blocked request headers (for public API)
  blockedHeaders: [
    'authorization',
    'cookie',
    'set-cookie',
    'x-auth-token',
    'x-api-key'
  ]
};

/**
 * Main API configuration with security defaults
 */
export const API_CONFIG = {
  // Base URL with security validation
  BASE_URL: getSecureBaseURL(),

  // Security-enhanced default headers
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Client-Version': '1.0.0',
    'X-API-Access-Level': 'public-only',
    'X-Security-Policy': 'strict',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    ...SECURITY_CONFIG.securityHeaders
  },

  // Security settings
  security: SECURITY_CONFIG,

  // Request configuration
  timeout: SECURITY_CONFIG.defaultTimeout,
  retries: 3,
  retryDelay: 1000, // Base delay in ms

  // Feature flags for security
  features: {
    rateLimiting: true,
    requestValidation: true,
    inputSanitization: true,
    csrfProtection: true,
    secureHeaders: true,
    httpsOnly: process.env.NODE_ENV === 'production'
  }
};

/**
 * API Endpoints configuration
 * Only includes public endpoints that don't require authentication
 */
export const API_ENDPOINTS = {
  // Base endpoints
  ROOT: '/',
  HEALTH: '/health',
  STATUS: '/status',

  // Issues (READ ONLY)
  ISSUES: '/issues',
  ISSUES_BY_STATUS: '/issues/status',
  ISSUES_CUSTOMER_SUMMARY: '/issues/customer',

  // Organizations (READ ONLY)
  ORGANIZATIONS: '/organizations',
  ORGANIZATIONS_CURRENT: '/organizations/current',
  ORGANIZATIONS_SUBDOMAIN: '/organizations/subdomain',
  ORGANIZATIONS_BRANDING: '/branding',
  ORGANIZATIONS_CONTACT: '/contact',
  ORGANIZATIONS_FEATURES: '/features',

  // Configurations (READ ONLY)
  CONFIGURATIONS: '/configurations',
  CONFIGURATIONS_ACTIVE_WORKTYPES: '/configurations/worktypes/active',

  // System endpoints (ALL REQUIRE AUTH - DISABLED)
  SYSTEM: '/system',
  SYSTEM_HEALTH: '/system/health',
  SYSTEM_METRICS: '/system/metrics',
  SYSTEM_CONNECTIONS: '/system/connections'
};

/**
 * Content Security Policy configuration
 */
export const CSP_CONFIG = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'unsafe-inline'"],
  'style-src': ["'self'", "'unsafe-inline'"],
  'img-src': ["'self'", 'data:', 'https:'],
  'connect-src': ["'self'", getSecureBaseURL()],
  'font-src': ["'self'"],
  'object-src': ["'none'"],
  'media-src': ["'self'"],
  'frame-src': ["'none'"],
  'worker-src': ["'self'"],
  'manifest-src': ["'self'"],
  'base-uri': ["'self'"],
  'form-action': ["'self'"],
  'frame-ancestors': ["'none'"],
  'upgrade-insecure-requests': process.env.NODE_ENV === 'production'
};

/**
 * Validate configuration on module load
 */
function validateConfiguration() {
  // Validate base URL
  try {
    const url = new URL(API_CONFIG.BASE_URL, window.location.origin);

    // Ensure HTTPS in production
    if (SECURITY_CONFIG.enforceHTTPS && url.protocol !== 'https:') {
      console.error('❌ SECURITY ERROR: HTTPS is required in production');
      throw new Error('HTTPS is required for security');
    }

    // Validate URL is not pointing to private networks
    const hostname = url.hostname;
    const privateNetworks = [
      /^127\./, /^10\./, /^172\.(1[6-9]|2[0-9]|3[01])\./, /^192\.168\./,
      /^169\.254\./, /localhost/i
    ];

    if (process.env.NODE_ENV === 'production' &&
        privateNetworks.some(pattern => pattern.test(hostname))) {
      console.error('❌ SECURITY ERROR: API URL points to private network');
      throw new Error('API URL must not point to private networks');
    }

  } catch (error) {
    if (process.env.NODE_ENV === 'production') {
      throw error;
    } else {
      console.warn('⚠️ Configuration warning:', error.message);
    }
  }
}

// Validate configuration
if (typeof window !== 'undefined') {
  validateConfiguration();
}

/**
 * Get Content Security Policy header value
 */
export function getCSPHeader() {
  return Object.entries(CSP_CONFIG)
    .map(([directive, sources]) => {
      if (directive === 'upgrade-insecure-requests') {
        return sources ? directive : '';
      }
      return `${directive} ${sources.join(' ')}`;
    })
    .filter(Boolean)
    .join('; ');
}

/**
 * Check if current environment is secure
 */
export function isSecureEnvironment() {
  if (typeof window === 'undefined') return true; // SSR

  return window.location.protocol === 'https:' ||
         window.location.hostname === 'localhost' ||
         process.env.NODE_ENV === 'development';
}

/**
 * Get security recommendations for current setup
 */
export function getSecurityRecommendations() {
  const recommendations = [];

  if (!isSecureEnvironment()) {
    recommendations.push({
      type: 'critical',
      message: 'Use HTTPS in production for secure communication'
    });
  }

  if (!SECURITY_CONFIG.csrfEnabled) {
    recommendations.push({
      type: 'warning',
      message: 'CSRF protection should be enabled'
    });
  }

  if (API_CONFIG.timeout > 60000) {
    recommendations.push({
      type: 'info',
      message: 'Consider reducing timeout for better user experience'
    });
  }

  return recommendations;
}

// Export security status for monitoring
export const SECURITY_STATUS = {
  level: 'public-only',
  httpsEnforced: SECURITY_CONFIG.enforceHTTPS,
  csrfEnabled: SECURITY_CONFIG.csrfEnabled,
  inputSanitization: true,
  rateLimiting: true,
  requestValidation: true,
  secureHeaders: true,
  configurationValid: true
};